import { z } from 'zod';
import { offerStatusSchema } from './offer.js';

/**
 * RUF-570: Mock escrow deposit gates offer completion.
 *
 * A buyer whose offer is `accepted` pays a mock partial deposit that SiliconX
 * holds in escrow; the deposit gates the sale's completion. All workflow state
 * lives in `offer.status` (`accepted` -> `deposit_held` -> `completed`);
 * `offer_payment` is detail-only (no join needed to know an offer's payment
 * state).
 *
 * SIL-2152: there is deliberately NO client-side deposit fraction. The deposit is
 * `escrow_rail_setting.deposit_fraction` (migration 1160, default 5%) of the accepted
 * price, applied inside the DB by `offer_pay_deposit` (mock rail) and the outbox trigger
 * (Escrow.com rail). The UI and the purchase-terms consent facts show the amount the DB
 * reports via `get_offer_deposit_quote` (GET /api/client/offers/:id/deposit-quote), so a
 * rate change never needs a client release.
 */

/** GET /api/client/offers/:id/deposit-quote - dollars, as the DB will (or did) write them. */
export interface OfferDepositQuote {
  priceUsd: number;
  depositUsd: number;
  balanceUsd: number;
}

export const offerPaymentKindSchema = z.enum([
  'escrow_deposit',
  'final',
  // SIL-645: amendment settlement — the deliberate separate step that
  // actually moves money for an accepted amendment's escrow delta. Never
  // created automatically by acceptance itself.
  'amendment_topup',
  'amendment_release',
]);
export type OfferPaymentKind = z.infer<typeof offerPaymentKindSchema>;

// SIL-1593: 'escrow_com' joins 'mock' (migration 1017 widened the DB CHECK).
export const offerPaymentMethodSchema = z.enum(['mock', 'escrow_com']);
export type OfferPaymentMethod = z.infer<typeof offerPaymentMethodSchema>;

export const offerPaymentStatusSchema = z.enum(['held', 'released', 'refunded']);
export type OfferPaymentStatus = z.infer<typeof offerPaymentStatusSchema>;

/** A payment record — detail-only; carries no workflow authority of its own. */
export const offerPaymentSchema = z.object({
  id: z.string().uuid(),
  offerId: z.string().uuid(),
  kind: offerPaymentKindSchema,
  method: offerPaymentMethodSchema,
  status: offerPaymentStatusSchema,
  amountUsd: z.number(),
  paymentMethodId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type OfferPayment = z.infer<typeof offerPaymentSchema>;

/**
 * Response for POST /api/client/offers/:id/pay-deposit — the created payment
 * plus the offer's new status, so the caller can update its UI without a
 * second round-trip.
 */
export const payDepositResponseSchema = z.object({
  payment: offerPaymentSchema,
  offerStatus: z.literal('deposit_held'),
});
export type PayDepositResponse = z.infer<typeof payDepositResponseSchema>;

/**
 * RUF-571: Ops escrow dashboard — GET /api/offer-payments row shape. Joins in
 * the offer/listing/buyer/seller context ops needs to see (and drill into)
 * without a second round-trip.
 */
export const offerPaymentOpsRowSchema = offerPaymentSchema.extend({
  refundReason: z.string().nullable(),
  listingId: z.string().uuid().nullable(),
  offerStatus: offerStatusSchema.nullable(),
  offerAmountUsd: z.number().nullable(),
  buyerOrgId: z.string().uuid().nullable(),
  buyerLegalName: z.string().nullable(),
  sellerOrgId: z.string().uuid().nullable(),
  sellerLegalName: z.string().nullable(),
});
export type OfferPaymentOpsRow = z.infer<typeof offerPaymentOpsRowSchema>;

/**
 * Request body for POST /api/offer-payments/:id/refund (ops_admin only) — a
 * reason is mandatory (mirrors the offer_refund_deposit SQL fn's own check;
 * this is the fast, friendly client-side echo of that guard).
 */
export const refundDepositRequestSchema = z.object({
  reason: z.string().trim().min(1, 'a refund reason is required'),
});
export type RefundDepositRequest = z.infer<typeof refundDepositRequestSchema>;

/** Response for POST /api/offer-payments/:id/refund. */
export const refundDepositResponseSchema = z.object({
  paymentId: z.string().uuid(),
  offerId: z.string().uuid(),
  paymentStatus: z.literal('refunded'),
  offerStatus: z.literal('accepted'),
});
export type RefundDepositResponse = z.infer<typeof refundDepositResponseSchema>;

/**
 * SIL-1593: the buyer's Escrow.com deposit transaction for an offer, from
 * GET /api/client/offers/:id/escrow (get_offer_escrow_pay_url). `payUrl` is
 * null until the transaction is open, or when Escrow.com could not be reached; when
 * present it is a freshly fetched https Escrow.com link (never persisted).
 */
export const escrowDepositViewSchema = z.object({
  transactionId: z.string().uuid(),
  // SIL-1598: the live balance once one exists (the buyer funds it with the same pay-link flow).
  kind: z.enum(['deposit', 'balance']),
  status: z.enum(['pending_create', 'open', 'closed', 'cancelled']),
  amountUsd: z.number(),
  // Fetched fresh per request (never stored); null while pending_create or on error.
  payUrl: z.string().nullable(),
  // Fixed code: escrow_link_unavailable = open but the link could not be fetched;
  // consent_required = the buyer must record purchase-terms consent first (SIL-1594).
  error: z.enum(['escrow_link_unavailable', 'consent_required']).optional(),
});
export type EscrowDepositView = z.infer<typeof escrowDepositViewSchema>;

/**
 * SIL-1598: the seller requests the balance (agreed price - deposit) once the deposit is secured.
 * The body is deliberately EMPTY and strict: the amount, parties and environment all come from the DB
 * (the deposit row), never from the caller.
 */
export const requestEscrowBalanceInputSchema = z.object({}).strict();
export const requestEscrowBalanceResponseSchema = z.object({
  result: z.enum(['requested', 'already_requested']),
});
export type RequestEscrowBalanceResponse = z.infer<typeof requestEscrowBalanceResponseSchema>;

/**
 * SIL-1598: seller-scoped balance status (GET /api/client/offers/:id/escrow/balance). No pay link, no buyer
 * email, no transaction id. `escrow: null` = not on the escrow rail (or not the seller).
 */
export const escrowBalanceStatusSchema = z.object({
  status: z.enum(['none', 'pending', 'link_sent', 'funded']),
  amountUsd: z.number().nullable(),
  offerStatus: z.string(),
});
export type EscrowBalanceStatus = z.infer<typeof escrowBalanceStatusSchema>;
